import { Address } from '@liberty-financial/agp-shared';

import { SparkAddress } from '@shared/models/address';
import {
  IdPrimitiveValueCollection,
  toIdPrimitiveValueCollection,
  toNativePrimitiveArray,
  OptionsPropertyDefinition,
  StringArrayPropertyDefinition,
  StringPropertyDefinition
} from '@shared/models/schema-property-definition';

import { Shareholder } from './shareholder';
import { ClientAddress } from '../address';
import { ContactType } from '../contactType';
import { ContactReferrerArrangement } from '../referrer-arrangement';

export * from './shareholder';

export namespace Company {
  export interface SchemaPropertyDefinition {
    name: StringPropertyDefinition;
    tradingName: StringPropertyDefinition;
    companyType: StringPropertyDefinition;
    businessNumber: StringPropertyDefinition;
    companyNumber: StringPropertyDefinition;
    startDate: StringPropertyDefinition;
    gstRegistrationDate: StringPropertyDefinition;
    businessNature: OptionsPropertyDefinition;
    accountantName: StringPropertyDefinition;
    accountantPhone: StringPropertyDefinition;
    address: ClientAddress.SchemaPropertyDefinition;
    registeredOffice: ClientAddress.SchemaPropertyDefinition;
    directors: StringArrayPropertyDefinition;
    contactType: OptionsPropertyDefinition;
    status: OptionsPropertyDefinition;
    referrerArrangement: ContactReferrerArrangement.SchemaPropertyDefinition;
  }

  export interface SchemaContract {
    name: string;
    tradingName: string | null;
    businessNumber: string | null;
    companyNumber: string | null;
    startDate: string | null;
    gstRegistrationDate: string | null;
    businessNature: string | null;
    accountantName: string | null;
    accountantPhone: string | null;
    address: SparkAddress.SchemaContract | null;
    registeredOffice: SparkAddress.SchemaContract | null;
    directors: string[];
    shareholders: IdPrimitiveValueCollection<number>;
    contactType: string;
    status: string;
    referrerArrangement?: ContactReferrerArrangement.SchemaContract | null;
    nmbAffiliateId?: string | null;
  }

  export interface CompanySummary {
    id: string;
    name: string;
  }

  export interface Model {
    id: string;
    name: string;
    tradingName?: string | null;
    businessNumber?: string | null;
    companyNumber?: string | null;
    startDate?: string | null;
    gstRegistrationDate?: string | null;
    businessNature?: string | null;
    accountantName?: string | null;
    accountantPhone?: string | null;
    address: Address | null;
    registeredOffice: Address | null;
    directors: string[];
    shareholders: Shareholder.Model[];
    contactType: ContactType;
    status: string;
    referrerArrangement: ContactReferrerArrangement.Model | null;
    nmbAffiliateId: string | null;
  }

  export function toModel(id: string, c: SchemaContract): Model {
    return {
      id,
      ...c,
      nmbAffiliateId: c.nmbAffiliateId ?? null,
      contactType: c.contactType as ContactType,
      shareholders: toNativePrimitiveArray(c.shareholders),
      address: SparkAddress.toModel(c.address),
      registeredOffice: SparkAddress.toModel(c.registeredOffice),
      referrerArrangement: ContactReferrerArrangement.toModel(
        c.referrerArrangement
      )
    };
  }

  export function toSchemaContract(c: Partial<Model>): Partial<SchemaContract> {
    return {
      ...c,
      shareholders: c.shareholders
        ? toIdPrimitiveValueCollection(c.shareholders)
        : undefined,
      address: c.address ? SparkAddress.toSchemaContract(c.address) : undefined,
      registeredOffice: c.registeredOffice
        ? SparkAddress.toSchemaContract(c.registeredOffice)
        : undefined,
      referrerArrangement: c.referrerArrangement
        ? ContactReferrerArrangement.toSchemaContract(c.referrerArrangement)
        : c.referrerArrangement,
    };
  }
}
